import { Dialog } from "@headlessui/react";
import { useState, useEffect } from "react";
import { pitchesApi } from "../../repositories/pitches";
import Button from "../ui/Button";
import { Skeleton } from "../ui/skeleton";
import { formCheckboxClass } from "../ui/formStyles";

interface DSPDeliveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (selectedDSPs: string[]) => void | Promise<void>;
  initialDSPs: string[];
  mode: 'admin' | 'user';
  pitchId?: number;
}

export default function DSPDeliveryModal({
  isOpen,
  onClose,
  onSubmit,
  initialDSPs,
  mode,
  pitchId,
}: DSPDeliveryModalProps) {
  const [selectedDSPs, setSelectedDSPs] = useState<string[]>(initialDSPs);
  const [deliveredDSPs, setDeliveredDSPs] = useState<string[]>([]);
  const [availableDSPs, setAvailableDSPs] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const DSPs = ["Spotify", "Apple", "Amazon", "TIDAL", "Deezer", "Audiomack", "SoundCloud","YouTube","SiriusXM"];

  useEffect(() => {
    if (isOpen) {
      setSelectedDSPs(initialDSPs);
      setSubmitting(false);
    }
  }, [isOpen]);

  // Fetch delivery data when modal opens (user mode for display, admin mode for the available-DSP allowlist)
  useEffect(() => {
    if (isOpen && pitchId) {
      fetchDeliveryData();
    }
  }, [isOpen, mode, pitchId]);

  const fetchDeliveryData = async () => {
    if (!pitchId) return;

    setLoading(true);
    try {
      const response = await pitchesApi.getDelivery(pitchId);
      const fetchedDSPs = (response.data.data.deliveredDSPs || []).filter((dsp: string) => dsp !== "");
      const allowed = (response.data.data.selectedDSPs || []).filter((dsp: string) => dsp !== "");

      if (mode === 'user') {
        setDeliveredDSPs(fetchedDSPs);
      } else if (mode === 'admin') {
        setAvailableDSPs(allowed);
        setDeliveredDSPs(fetchedDSPs);
        if (initialDSPs.length === 0) {
          // First-time delivery: pre-check whatever the server already has
          setSelectedDSPs(fetchedDSPs);
        }
      }
    } catch (error) {
      console.error('Failed to fetch delivery data:', error);
      if (mode === 'user') {
         setDeliveredDSPs(initialDSPs.filter(dsp => dsp !== ""));
      }
    } finally {
      setLoading(false);
    }
  };

  const isDSPAvailable = (dsp: string) => availableDSPs.includes(dsp);

  const toggleDSP = (dsp: string) => {
    if (mode !== 'admin' || !isDSPAvailable(dsp)) return;
    setSelectedDSPs((prev) =>
      prev.includes(dsp) ? prev.filter((item) => item !== dsp) : [...prev, dsp]
    );
  };

  const handleSubmit = async () => {
    if (mode !== 'admin' || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(selectedDSPs);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Dialog.Panel className="bg-white rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <Dialog.Title className="text-lg font-semibold mb-4">
          {mode === 'admin' ? 'Select DSPs for Delivery' : 'Pitch Delivered To'}
        </Dialog.Title>
        
        <div className="space-y-2">
          {mode === 'admin' ? (
            // Admin mode: checkboxes for selection
            loading ? (
              // One placeholder row per DSP checkbox.
              <div className="space-y-3 py-1" aria-busy="true">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Skeleton className="w-4 h-4 rounded" />
                    <Skeleton className="h-4 w-28" />
                  </div>
                ))}
              </div>
            ) : (
              DSPs.map((dsp) => {
                const available = isDSPAvailable(dsp);
                return (
                  <label
                    key={dsp}
                    title={available ? "" : "User has not selected this DSP in Pitch Submission"}
                    className={`flex items-center space-x-2 ${available ? '' : 'opacity-50 cursor-not-allowed'}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDSPs.includes(dsp)}
                      onChange={() => toggleDSP(dsp)}
                      disabled={!available}
                      className={formCheckboxClass}
                    />
                    <span>{dsp}</span>
                  </label>
                );
              })
            )
          ) : (
            // User mode: display delivered DSPs from API
            <div className="space-y-2">
              {loading ? (
                <div className="space-y-2" aria-busy="true">
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} className="w-full h-9" />
                  ))}
                </div>
              ) : deliveredDSPs.length > 0 ? (
                deliveredDSPs.map((dsp) => (
                  <div key={dsp} className="flex items-center space-x-2 p-2 bg-success-50 rounded-md">
                    <div className="w-2 h-2 bg-success-500 rounded-full"></div>
                    <span className="text-success-800 font-medium">{dsp}</span>
                  </div>
                ))
              ) : (
                <p className="text-neutral-500 text-center py-4">No DSPs selected for delivery</p>
              )}
            </div>
          )}
        </div>
        
        <div className="flex items-center justify-end gap-2 mt-6">
          <div className="flex gap-2">
            <Button
              variant="neutral"
              onClick={onClose}
              disabled={submitting}
            >
              {mode === 'admin' ? 'Cancel' : 'Close'}
            </Button>
            {/* Kept in place (disabled) while loading so the footer doesn't shift. */}
            {mode === 'admin' && (
              <Button
                onClick={handleSubmit}
                disabled={loading || selectedDSPs.length === 0 || submitting}
              >
                {submitting && (
                  <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                )}
                {submitting ? 'Updating...' : 'Update Delivery'}
              </Button>
            )}
          </div>
        </div>
      </Dialog.Panel>
    </Dialog>
  );
}